import React, { useCallback, useMemo, useState } from 'react';
import {
  FlatList,
  Image,
  RefreshControl,
  StatusBar,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import TonyteBackground from '../components/TonyteBackground';
import AmbientGlow from '../components/AmbientGlow';
import LoadingSpinner from '../components/LoadingSpinner';
import { Colors, Radius, Typography } from '../theme';
import type { RootStackParamList, VenueSummary } from '../types';
import { buildPartnerVenueSummaries, searchVenues, useVenueVibes, VENUE_CATEGORIES } from '../hooks/useVenueVibes';
import { auth } from '../firebase';
import { useProfile } from '../hooks/useProfile';
import { useI18n } from '../i18n';
import { canAccessVenueVibes } from '../utils/roles';
import { useTheme } from '../theme/useTheme';
import { daylightStyle } from '../theme/daylightStyle';
import type { TonyteTheme } from '../theme/types';

type Props = NativeStackScreenProps<RootStackParamList, 'VibeVenues'>;

// Midnight stays the canonical StyleSheet below; Daylight only overrides.
function useAppearance() {
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);
  const icon = (dark: string) => (theme.isDark ? dark : theme.colors.icon);
  const iconMuted = (dark: string) => (theme.isDark ? dark : theme.colors.iconMuted);
  return { theme, appearance, icon, iconMuted };
}

const CATEGORIES = ['All', ...VENUE_CATEGORIES];

const formatCount = (value: number) => {
  if (value >= 1000) {
    const formatted = (value / 1000).toFixed(1).replace(/\.0$/, '');
    return `${formatted}k`;
  }
  return String(value);
};

const VenueProfileCard = React.memo(function VenueProfileCard({
  venue,
  onPress,
}: {
  venue: VenueSummary;
  onPress: (venue: VenueSummary) => void;
}) {
  const { appearance, iconMuted } = useAppearance();
  const handlePress = useCallback(() => onPress(venue), [onPress, venue]);
  const followersCount = typeof venue.followersCount === 'number'
    ? venue.followersCount
    : venue.fallbackFollowersCount || 0;
  return (
    <TouchableOpacity activeOpacity={0.9} onPress={handlePress} style={[styles.card, appearance.card]}>
      <View style={styles.cardCover}>
        {venue.coverUrl ? (
          <Image source={{ uri: venue.coverUrl }} style={StyleSheet.absoluteFill} resizeMode="cover" />
        ) : (
          <LinearGradient colors={['#2F145F', '#111827', '#060711']} style={[StyleSheet.absoluteFill, styles.cardCoverFallback]}>
            <Text style={styles.cardCoverInitial}>{venue.name.slice(0, 1).toUpperCase()}</Text>
          </LinearGradient>
        )}
      </View>
      <View style={styles.cardBody}>
        <Text style={[styles.cardName, appearance.textPrimary]} numberOfLines={1}>{venue.name}</Text>
        <Text style={[styles.cardMeta, appearance.textSecondary]} numberOfLines={1}>{[venue.category, venue.city].filter(Boolean).join(' · ')}</Text>
        <View style={styles.cardStats}>
          <View style={styles.cardStat}>
            <Icon name="users" size={11} color={iconMuted('rgba(255,255,255,0.7)')} />
            <Text style={[styles.cardStatText, appearance.statText]}>{formatCount(followersCount)}</Text>
          </View>
          <View style={styles.cardStat}>
            <Icon name="calendar" size={11} color={iconMuted('rgba(255,255,255,0.7)')} />
            <Text style={[styles.cardStatText, appearance.statText]}>{venue.upcomingCount} upcoming</Text>
          </View>
        </View>
      </View>
      <Icon name="chevron-right" size={18} color={iconMuted('rgba(255,255,255,0.45)')} />
    </TouchableOpacity>
  );
});

const CategoryChip = React.memo(function CategoryChip({
  item,
  active,
  onSelect,
}: {
  item: string;
  active: boolean;
  onSelect: (item: string) => void;
}) {
  const { appearance } = useAppearance();
  const handlePress = useCallback(() => onSelect(item), [onSelect, item]);
  return (
    <TouchableOpacity onPress={handlePress} style={[styles.chip, appearance.chip, active && styles.chipActive, active && appearance.chipActive]}>
      <Text style={[styles.chipText, appearance.chipText, active && styles.chipTextActive, active && appearance.chipTextActive]}>{item}</Text>
    </TouchableOpacity>
  );
});

// Extracted so typing in the search box (which must re-render this header)
// doesn't also force the FlatList's data rows to reconcile as part of the
// same tree, and so the category chip row keeps a stable renderItem.
const VenuesHeader = React.memo(function VenuesHeader({
  onBack,
  search,
  onSearchChange,
  category,
  onCategoryChange,
  error,
  topInset,
}: {
  onBack: () => void;
  search: string;
  onSearchChange: (value: string) => void;
  category: string;
  onCategoryChange: (value: string) => void;
  error: string | null;
  topInset: number;
}) {
  const { t } = useI18n();
  const { appearance, icon, iconMuted, theme } = useAppearance();
  const renderChip = useCallback(({ item }: { item: string }) => (
    <CategoryChip item={item} active={item === category} onSelect={onCategoryChange} />
  ), [category, onCategoryChange]);

  return (
    <View style={[styles.header, { paddingTop: topInset + 12 }]}>
      <View style={styles.navRow}>
        <TouchableOpacity style={[styles.iconButton, appearance.control]} onPress={onBack}>
          <Icon name="chevron-left" size={22} color={icon('#fff')} />
        </TouchableOpacity>
        <Text style={[styles.brand, appearance.textSecondary]}>Tonyte</Text>
      </View>
      <Text style={[styles.title, appearance.textPrimary]}>{t('venueProfiles.vibesTitle')}</Text>
      <Text style={[styles.subtitle, appearance.textSecondary]}>{t('venueProfiles.vibesSubtitle')}</Text>
      <View style={[styles.searchBar, appearance.input]}>
        <Icon name="search" size={16} color={iconMuted('rgba(255,255,255,0.56)')} />
        <TextInput
          value={search}
          onChangeText={onSearchChange}
          placeholder={t('venueProfiles.search')}
          placeholderTextColor={theme.isDark ? 'rgba(255,255,255,0.42)' : theme.colors.textPlaceholder}
          style={[styles.searchInput, appearance.textPrimary]}
          autoCorrect={false}
        />
      </View>
      <FlatList
        horizontal
        showsHorizontalScrollIndicator={false}
        data={CATEGORIES}
        keyExtractor={item => item}
        contentContainerStyle={styles.chips}
        renderItem={renderChip}
      />
      {error ? <Text style={[styles.errorText, appearance.warning]}>{t('venueProfiles.refreshFailed')}</Text> : null}
    </View>
  );
});

const VibeVenuesContent: React.FC<Props> = ({ navigation }) => {
  const { t } = useI18n();
  const insets = useSafeAreaInsets();
  const { appearance, theme } = useAppearance();
  const { events, venueProfiles, loading, error, refresh } = useVenueVibes();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');

  // Only venues backed by a real partner account (e.g. InterLokal), shown as
  // profiles — not venues synthesized from bulk-imported city events.
  const { profile } = useProfile(auth.currentUser?.uid || null);
  const isAdmin = profile?.role === 'admin' || profile?.role === 'co-admin';
  const venues = useMemo(
    () => buildPartnerVenueSummaries(events, venueProfiles).filter(venue => !venue.adminOnly || isAdmin),
    [events, isAdmin, venueProfiles],
  );
  const filteredVenues = useMemo(() => searchVenues(venues, search, category), [category, search, venues]);

  const openVenue = useCallback((venue: VenueSummary) => {
    navigation.navigate('VenueProfile', { venueId: venue.id, venue });
  }, [navigation]);

  const handleBack = useCallback(() => navigation.goBack(), [navigation]);

  const renderVenue = useCallback(({ item }: { item: VenueSummary }) => (
    <VenueProfileCard venue={item} onPress={openVenue} />
  ), [openVenue]);

  if (loading && venues.length === 0) return <LoadingSpinner />;

  return (
    <TonyteBackground appearanceAware>
      <StatusBar barStyle={theme.statusBarStyle} translucent backgroundColor="transparent" />
      {theme.isDark ? (
        <>
          <AmbientGlow tone="teal" size={250} intensity={0.22} style={styles.topGlow} />
          <AmbientGlow tone="orange" size={280} intensity={0.18} style={styles.bottomGlow} />
        </>
      ) : null}
      <FlatList
        data={filteredVenues}
        keyExtractor={item => item.id}
        contentContainerStyle={styles.list}
        refreshControl={<RefreshControl refreshing={loading} onRefresh={refresh} tintColor={theme.isDark ? '#fff' : theme.colors.accent} />}
        ListHeaderComponent={(
          <VenuesHeader
            onBack={handleBack}
            search={search}
            onSearchChange={setSearch}
            category={category}
            onCategoryChange={setCategory}
            error={error}
            topInset={insets.top}
          />
        )}
        renderItem={renderVenue}
        ListEmptyComponent={
          <View style={styles.empty}>
            <Icon name="map-pin" size={28} color={Colors.purple} />
            <Text style={[styles.emptyTitle, appearance.textPrimary]}>{t('venueProfiles.noVenues')}</Text>
            <Text style={[styles.emptyText, appearance.textSecondary]}>{t('venueProfiles.noVenuesSub')}</Text>
          </View>
        }
        ListFooterComponent={<View style={{ height: insets.bottom + 26 }} />}
        showsVerticalScrollIndicator={false}
      />
    </TonyteBackground>
  );
};

const VibeVenuesScreen: React.FC<Props> = props => {
  const insets = useSafeAreaInsets();
  const { t } = useI18n();
  const { profile, loading } = useProfile(auth.currentUser?.uid || null);
  const { appearance, icon, theme } = useAppearance();

  if (loading) return <LoadingSpinner />;
  if (canAccessVenueVibes(profile?.role)) return <VibeVenuesContent {...props} />;

  return (
    <TonyteBackground appearanceAware style={styles.accessBlocked}>
      <StatusBar barStyle={theme.statusBarStyle} translucent backgroundColor="transparent" />
      {theme.isDark ? <AmbientGlow tone="blue" size={360} intensity={0.28} style={styles.blockedGlow} /> : null}
      <TouchableOpacity
        onPress={() => props.navigation.goBack()}
        style={[styles.blockedBack, appearance.control, { top: insets.top + 16 }]}
        activeOpacity={0.8}
      >
        <Icon name="arrow-left" size={20} color={icon('#fff')} />
      </TouchableOpacity>
      <View style={styles.blockedContent}>
        <View style={styles.blockedIcon}>
          <Icon name="tool" size={34} color={Colors.purple} />
        </View>
        <Text style={[styles.blockedTitle, appearance.textPrimary]}>{t('discover.underConstruction')}</Text>
        <Text style={[styles.blockedText, appearance.textSecondary]}>{t('discover.venueTesterUnavailable')}</Text>
      </View>
    </TonyteBackground>
  );
};

const styles = StyleSheet.create({
  accessBlocked: {
    flex: 1,
    backgroundColor: '#050914',
  },
  blockedGlow: {
    top: -120,
    right: -120,
  },
  blockedBack: {
    position: 'absolute',
    left: 20,
    zIndex: 2,
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  blockedContent: {
    flex: 1,
    paddingHorizontal: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  blockedIcon: {
    width: 82,
    height: 82,
    borderRadius: 41,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(192,132,252,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(192,132,252,0.28)',
    marginBottom: 24,
  },
  blockedTitle: {
    color: '#fff',
    fontSize: 30,
    fontWeight: Typography.extrabold,
    textAlign: 'center',
  },
  blockedText: {
    marginTop: 12,
    color: 'rgba(255,255,255,0.62)',
    fontSize: Typography.base,
    lineHeight: 23,
    textAlign: 'center',
  },
  topGlow: { top: -120, right: -110 },
  bottomGlow: { bottom: -150, left: -120 },
  header: {
    paddingHorizontal: 18,
    paddingBottom: 16,
  },
  navRow: {
    height: 40,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  iconButton: {
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  brand: {
    color: 'rgba(255,255,255,0.72)',
    fontSize: 13,
    fontWeight: Typography.bold,
  },
  title: {
    color: '#fff',
    fontSize: 32,
    lineHeight: 36,
    fontWeight: Typography.extrabold,
    letterSpacing: 0,
    marginTop: 12,
  },
  subtitle: {
    color: 'rgba(255,255,255,0.68)',
    fontSize: 15,
    lineHeight: 21,
    marginTop: 6,
  },
  searchBar: {
    marginTop: 18,
    height: 48,
    borderRadius: Radius.pill,
    paddingHorizontal: 15,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: 'rgba(255,255,255,0.09)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
  },
  searchInput: {
    flex: 1,
    color: '#fff',
    fontSize: 14,
    paddingVertical: 0,
  },
  chips: {
    gap: 8,
    paddingTop: 14,
    paddingRight: 20,
  },
  chip: {
    height: 34,
    borderRadius: Radius.pill,
    paddingHorizontal: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  chipActive: {
    backgroundColor: 'rgba(168,85,247,0.24)',
    borderColor: 'rgba(216,180,254,0.52)',
  },
  chipText: {
    color: 'rgba(255,255,255,0.68)',
    fontSize: 12,
    fontWeight: Typography.bold,
  },
  chipTextActive: { color: '#fff' },
  list: { paddingHorizontal: 16 },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 10,
    padding: 10,
    borderRadius: 16,
    backgroundColor: '#101722',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
  },
  // Covers are 2.5:1 (see venues/{id}.coverUrl), so this shows them uncropped.
  cardCover: { width: 160, height: 64, borderRadius: 10, overflow: 'hidden', backgroundColor: '#111827' },
  cardCoverFallback: { alignItems: 'center', justifyContent: 'center' },
  cardCoverInitial: { color: '#fff', fontSize: 24, fontWeight: Typography.extrabold },
  cardBody: { flex: 1, minWidth: 0 },
  cardName: { color: '#fff', fontSize: 15, fontWeight: Typography.extrabold },
  cardMeta: { color: 'rgba(255,255,255,0.6)', fontSize: 12, marginTop: 2 },
  cardStats: { flexDirection: 'row', alignItems: 'center', gap: 12, marginTop: 6 },
  cardStat: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  cardStatText: { color: 'rgba(255,255,255,0.8)', fontSize: 11, fontWeight: Typography.bold },
  empty: { paddingVertical: 72, alignItems: 'center', paddingHorizontal: 30 },
  emptyTitle: { color: '#fff', fontSize: 18, fontWeight: Typography.bold, marginTop: 12 },
  emptyText: { color: 'rgba(255,255,255,0.62)', fontSize: 13, marginTop: 5, textAlign: 'center' },
  errorText: { color: Colors.warning, fontSize: 12, marginTop: 10 },
});

const createThemeStyles = (theme: TonyteTheme) => StyleSheet.create({
  card: daylightStyle(theme, {
    backgroundColor: theme.colors.cardBackground,
    borderColor: theme.colors.cardBorder,
    ...theme.shadows.card,
  }),
  control: daylightStyle(theme, {
    backgroundColor: theme.colors.controlBackground,
    borderColor: theme.colors.border,
  }),
  input: daylightStyle(theme, {
    backgroundColor: theme.colors.inputBackground,
    borderColor: theme.colors.border,
  }),
  chip: daylightStyle(theme, {
    backgroundColor: theme.colors.chipBackground,
    borderColor: theme.colors.border,
  }),
  chipActive: daylightStyle(theme, {
    backgroundColor: theme.colors.chipBackgroundActive,
    borderColor: 'rgba(242,125,38,0.36)',
  }),
  chipText: daylightStyle(theme, { color: theme.colors.chipText }),
  chipTextActive: daylightStyle(theme, { color: theme.colors.filterActiveIcon }),
  textPrimary: daylightStyle(theme, { color: theme.colors.textPrimary }),
  textSecondary: daylightStyle(theme, { color: theme.colors.textSecondary }),
  statText: daylightStyle(theme, { color: theme.colors.actionLabel }),
  warning: daylightStyle(theme, { color: theme.colors.warning }),
});

export default VibeVenuesScreen;
